import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { SceneNames } from '@Core/SceneNames';
import ParticleEmitter from '@Core/Win/Elements/Emitter';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { Assets, ParticleContainer } from 'pixi.js';
import { GameScene } from '..';

export class StartupPreloader extends GameComponent {
    private continueBtn: boolean = true;
    private pages: SpineWrapper;
    private background: SpineWrapper;
    private pageAmount: number = 0;
    private currentPage: number = 0;
    private shouldPlayNextPage: boolean = true;
    private preloadingFinished: boolean = false;
    private logoAnimationFinished: boolean = false;
    private readonly spinesWithCustomSlots: SpineWrapper[] = [];
    private particleEmitter?: ParticleEmitter;

    constructor(public scene: GameScene) {
        super(scene);
        Logger.log(LoggerLevel.Constructor, { message: 'StartupPreloader constructor', source: LoggerSource.Game });
    }

    init(): void {
        this.game.scaleManager.onResize(this.scene);
        EventsCenter.on(
            EGameEvents.ASSETS_PRELOAD_FINISHED,
            () => {
                this.preloadingFinished = true;
                this.create();
            },
            this
        );
        EventsCenter.on(
            EGameEvents.PRELOADER_LOGO_ANIMATION_COMPLETED,
            () => {
                this.logoAnimationFinished = true;
                this.create();
            },
            this
        );
        EventsCenter.on(EGameEvents.PRELOADER_INFO_CONTINUE, () => this.destroy());
        EventsCenter.on(EGameEvents.ASSETS_LOAD_FINISHED, () => this.showContinueButton());
    }

    create(): void {
        if (!this.preloadingFinished || !this.logoAnimationFinished) return;
        this.createBackgroundSpine();
        this.createPreloaderSpine();
        this.pageAmount = this.pages.eventManager.countWildcardAnimations(EGameEvents.PAGE_$);
        this.startPreloaderInfoPageAnimation();
        this.game.gameStore.set(
            EGameStoreKeys.PreloaderState,
            { active: true, currentScene: SceneNames.PRELOADER_INFO, showPreloaderInfo: false, availableActions: { continue: this.continueBtn } },
            true
        );
    }

    private createTestParticleEmitter(): void {
        const spritesheet = Assets.get('coinsheet-json');

        if (!spritesheet) {
            throw new Error('coinsheet-json is not loaded');
        }

        const textures = Array.from({ length: 12 }, (_, index) => {
            const frameName = `coinsheet_${String(index + 1).padStart(3, '0')}`;
            const texture = spritesheet.textures?.[frameName];

            if (!texture) {
                throw new Error(`Missing coin texture: ${frameName}`);
            }

            return texture;
        });

        const config = {
            name: 'coinShower',
            atlas: 'coinsheet',
            prefix: 'coinsheet_',
            frames: 8,
            frameRate: 8,
            landscapeX: 960,
            landscapeY: 1100,
            portraitX: 540,
            portraitY: 1460,
            quantity: 1,
            gravityY: 1000,
            lifespan: { min: 1000, max: 3000 },
            rotate: { min: 180, max: 360 },
            scale: { start: 1, end: 0.5 },
            speed: { min: 400, max: 600 },
            layer: 'under'
        };

        const particleContainer = new ParticleContainer({
            position: { x: this.scene.screen.x + this.scene.screen.width / 2, y: this.scene.screen.y + this.scene.screen.height / 2 }
        });
        this.scene.stage.addChild(particleContainer);

        this.particleEmitter = new ParticleEmitter(0, config.landscapeY, particleContainer, config);
    }

    private showContinueButton(): void {
        this.continueBtn = true;

        this.game.gameStore.set(
            EGameStoreKeys.PreloaderState,
            { currentScene: SceneNames.PRELOADER_INFO, availableActions: { continue: this.continueBtn } },
            true
        );
    }

    private createPreloaderSpine(): void {
        this.pages = new SpineWrapper(
            this.scene,
            {
                x: this.scene.screen.x + this.scene.screen.width / 2,
                y: this.scene.screen.y + this.scene.screen.height / 2
            },
            {
                dataKey: 'preloaderSpine',
                skinName: 'default'
            },
            true
        )!;
    }

    private createBackgroundSpine(): void {
        this.background = new SpineWrapper(
            this.scene,
            {
                x: this.scene.screen.x + this.scene.screen.width / 2,
                y: this.scene.screen.y + this.scene.screen.height / 2
            },
            {
                dataKey: 'preloaderBackground',
                skinName: 'default'
            },
            true
        )!;
    }

    private async startPreloaderInfoPageAnimation(): Promise<void> {
        EventsCenter.emit(EGameEvents.PRELOADER_INFO_READY);
        await this.pages.waitForAnimationEvent(EGameEvents.PRELOADER_INFO_READY, 'complete').promise;
        this.nextPage();
    }

    private async nextPage(): Promise<void> {
        this.currentPage = (this.currentPage % this.pageAmount) + 1;
        EventsCenter.emit(EGameEvents.PAGE_$, { eventIds: [this.currentPage] });

        if (this.shouldPlayNextPage) {
            await this.pages.waitForAnimationEvent(EGameEvents.PAGE_$, 'complete', [this.currentPage]).promise;
            this.nextPage();
        }
    }

    private destroy(): void {
        this.pages.spine.visible = false;
        this.background.spine.visible = false;
        this.shouldPlayNextPage = false;
    }
}
